import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useOutletContext, useParams } from 'react-router';
import type { MembershipSummary } from '../../../../src/shared/auth';
import type {
  AvailabilityRule,
  AvailabilityRuleUpsert,
  Weekday,
} from '../../../../src/shared/scheduling';
import { WEEKDAYS } from '../../../../src/shared/scheduling';
import {
  schedulingKeys,
  fetchAvailabilityRules,
  updateAvailabilityRules,
} from '../../../api/scheduling';
import { ErrorNotice } from '../../../components/ErrorNotice';
import { LoadingScreen } from '../../../components/LoadingScreen';

const WEEKDAY_LABELS: Record<Weekday, string> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function parseTimeToMinutes(time: string): number {
  const parts = time.split(':').map(Number);
  const h = parts[0] ?? 0;
  const m = parts[1] ?? 0;
  return h * 60 + m;
}

interface RuleFormEntry {
  weekday: Weekday;
  enabled: boolean;
  startMinute: number;
  endMinute: number;
  slotMinutes: number;
}

const DEFAULT_SLOT_MINUTES = 30;
const DEFAULT_START = 9 * 60;
const DEFAULT_END = 17 * 60;

function buildFormEntries(rules: AvailabilityRule[]): RuleFormEntry[] {
  return WEEKDAYS.map((weekday) => {
    const existing = rules.find((r) => r.weekday === weekday);
    return {
      weekday,
      enabled: existing !== undefined,
      startMinute: existing?.startMinute ?? DEFAULT_START,
      endMinute: existing?.endMinute ?? DEFAULT_END,
      slotMinutes: existing?.slotMinutes ?? DEFAULT_SLOT_MINUTES,
    };
  });
}

function AvailabilityRulesForm({
  slug,
  initialRules,
  onSaved,
}: {
  slug: string;
  initialRules: AvailabilityRule[];
  onSaved: () => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [entries, setEntries] = useState<RuleFormEntry[]>(() => buildFormEntries(initialRules));

  const saveMutation = useMutation({
    mutationFn: (upserts: AvailabilityRuleUpsert[]) => updateAvailabilityRules(slug, upserts),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: schedulingKeys.availabilityRules.all(slug) });
      onSaved();
    },
  });

  const updateEntry = (weekday: Weekday, partial: Partial<RuleFormEntry>) => {
    setEntries((prev) => prev.map((e) => (e.weekday === weekday ? { ...e, ...partial } : e)));
  };

  const handleSave = (event: FormEvent) => {
    event.preventDefault();
    const upserts: AvailabilityRuleUpsert[] = entries
      .filter((e) => e.enabled)
      .map((e) => ({
        weekday: e.weekday,
        startMinute: e.startMinute,
        endMinute: e.endMinute,
        slotMinutes: e.slotMinutes,
      }));
    saveMutation.mutate(upserts);
  };

  return (
    <form onSubmit={handleSave} className="space-y-4">
      <div className="space-y-2">
        {entries.map((entry) => (
          <div
            key={entry.weekday}
            className={`rounded-lg border p-4 transition-colors ${
              entry.enabled ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50'
            }`}
            data-testid={`rule-row-${entry.weekday}`}
          >
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id={`day-enabled-${entry.weekday}`}
                checked={entry.enabled}
                onChange={(e) => updateEntry(entry.weekday, { enabled: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300"
                data-testid={`day-checkbox-${entry.weekday}`}
              />
              <label
                htmlFor={`day-enabled-${entry.weekday}`}
                className={`text-sm font-medium ${entry.enabled ? 'text-slate-900' : 'text-slate-400'}`}
              >
                {WEEKDAY_LABELS[entry.weekday]}
              </label>
            </div>

            {entry.enabled && (
              <div className="mt-3 grid grid-cols-3 gap-3">
                <div>
                  <label
                    htmlFor={`start-${entry.weekday}`}
                    className="block text-xs font-medium text-slate-600"
                  >
                    Start
                  </label>
                  <input
                    id={`start-${entry.weekday}`}
                    type="time"
                    value={formatMinutes(entry.startMinute)}
                    onChange={(e) =>
                      updateEntry(entry.weekday, {
                        startMinute: parseTimeToMinutes(e.target.value),
                      })
                    }
                    className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm shadow-sm focus:border-slate-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label
                    htmlFor={`end-${entry.weekday}`}
                    className="block text-xs font-medium text-slate-600"
                  >
                    End
                  </label>
                  <input
                    id={`end-${entry.weekday}`}
                    type="time"
                    value={formatMinutes(entry.endMinute)}
                    onChange={(e) =>
                      updateEntry(entry.weekday, { endMinute: parseTimeToMinutes(e.target.value) })
                    }
                    className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm shadow-sm focus:border-slate-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label
                    htmlFor={`slot-${entry.weekday}`}
                    className="block text-xs font-medium text-slate-600"
                  >
                    Slot (min)
                  </label>
                  <input
                    id={`slot-${entry.weekday}`}
                    type="number"
                    min={5}
                    max={240}
                    value={entry.slotMinutes}
                    onChange={(e) =>
                      updateEntry(entry.weekday, {
                        slotMinutes: Math.max(1, Number(e.target.value)),
                      })
                    }
                    className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm shadow-sm focus:border-slate-500 focus:outline-none"
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {saveMutation.isError && <ErrorNotice error={saveMutation.error} />}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saveMutation.isPending}
          className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="save-rules-btn"
        >
          {saveMutation.isPending ? 'Saving…' : 'Save availability'}
        </button>
        {saveMutation.isSuccess && (
          <p className="self-center text-sm text-green-600" data-testid="save-success">
            Availability saved.
          </p>
        )}
      </div>
    </form>
  );
}

export function AvailabilityRulesPage(): React.JSX.Element {
  const { slug } = useParams<'slug'>();
  const { membership } = useOutletContext<{ membership: MembershipSummary }>();

  const rulesQuery = useQuery({
    queryKey: schedulingKeys.availabilityRules.all(slug!),
    queryFn: ({ signal }) => fetchAvailabilityRules(slug!, signal),
    enabled: slug !== undefined,
  });

  if (slug === undefined) {
    return <p className="text-sm text-slate-500">Workspace slug not found.</p>;
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight">Availability Rules</h1>
      <p className="mt-1 text-sm text-slate-500">
        {membership.name} &middot; Set your weekly working hours for the scheduler.
      </p>

      {rulesQuery.isPending && (
        <div className="mt-4">
          <LoadingScreen />
        </div>
      )}

      {rulesQuery.isError && (
        <div className="mt-4">
          <ErrorNotice error={rulesQuery.error} />
        </div>
      )}

      {rulesQuery.isSuccess && (
        <div className="mt-6 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <AvailabilityRulesForm
            slug={slug}
            initialRules={rulesQuery.data.rules}
            onSaved={() => {}}
          />
        </div>
      )}
    </div>
  );
}
